import { useEffect, useRef, useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GripVertical, Plus, Trash2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { SettingsField, SettingsGroup, SettingsHint } from '../../../components/design-system/common/SettingsPanel';
import { useAutosave } from '../../../common/hooks/use-autosave.hook';
import {
    setInitialTheme,
    setVisualizerFilters as setVisualizerFiltersAction
} from '../../../redux/vendor/settings/settingsActions';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';

/**
 * One row of the active-filters list. The whole row is the drag target (5px
 * activation distance), so the remove button stops pointerdown from reaching
 * the sensor — otherwise a click on it could start a drag.
 */
const SortableFilterRow = ({ id, filter, index, onRemove, removeLabel }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1000 : 1,
        opacity: isDragging ? 0.8 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={cn(
                'relative flex h-10 select-none items-center gap-2 bg-background ps-2 pe-1 text-sm text-foreground transition-colors hover:bg-muted/50',
                isDragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
        >
            <GripVertical className="size-4 shrink-0 text-muted-foreground" />
            <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
            <span className="min-w-0 flex-1 truncate">{filter.label}</span>
            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-muted-foreground"
                aria-label={removeLabel}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(filter.name);
                }}
            >
                <X className="size-4" />
            </Button>
        </div>
    );
};

// Removable option chip — used for select-field options on both existing and new fields
const OptionChip = ({ label, onDelete }: { label: string; onDelete: () => void }) => (
    <span className="inline-flex h-6 items-center gap-1 rounded-md border bg-background ps-2 pe-1 text-xs text-foreground">
        {label}
        <button
            type="button"
            onClick={onDelete}
            className="text-muted-foreground transition-colors hover:text-destructive"
        >
            <X className="size-3" />
        </button>
    </span>
);

/**
 * The options of a "select" custom field: the chips so far, and an input that
 * adds one on Enter. Shared by existing fields and the new-field draft.
 */
const OptionsEditor = ({
    label,
    options,
    value,
    placeholder,
    onValueChange,
    onAdd,
    onRemove,
}: {
    label: string;
    options: string[];
    value: string;
    placeholder: string;
    onValueChange: (value: string) => void;
    onAdd: () => void;
    onRemove: (index: number) => void;
}) => (
    <SettingsField label={label}>
        <div className="flex flex-wrap items-center gap-1.5">
            {options.map((option, optionIndex) => (
                <OptionChip key={optionIndex} label={option} onDelete={() => onRemove(optionIndex)} />
            ))}
            <Input
                placeholder={placeholder}
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
                className="h-7 w-[140px] text-xs"
            />
        </div>
    </SettingsField>
);

export const AdvancedSettings = (_props: { inTab?: boolean }) => {
    const { t } = useTranslation();

    // Custom fields configuration state
    const [customFields, setCustomFields] = useState([]);
    const [newCustomField, setNewCustomField] = useState({
        fieldName: '',
        fieldType: 'text',
        fieldLabel: '',
        required: false,
        options: [],
        defaultValue: '',
        placeholder: ''
    });
    const [newOptionValue, setNewOptionValue] = useState('');
    const [editingFieldOptionValues, setEditingFieldOptionValues] = useState({});

    // Visualizer filters configuration state
    const [visualizerFilters, setVisualizerFilters] = useState<string[]>(['Color', 'Size', 'Collection']);

    // Track initial values to detect changes
    const [initialCustomFields, setInitialCustomFields] = useState([]);
    const [initialVisualizerFilters, setInitialVisualizerFilters] = useState<string[]>(['Color', 'Size', 'Collection']);

    // Loading state for custom fields
    const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(true);

    // dnd-kit sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // 5px movement required before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const configLoadedRef = useRef(false) // Prevent multiple config loads
    /** Set once the stored config has loaded: nothing is saved before that. */
    const [configLoaded, setConfigLoaded] = useState(false)

    const settingsAPI = useSettingsApi()
    const productsAPI = useProductsApi()
    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(setVisualizerFiltersAction(visualizerFilters));
    }, [visualizerFilters, dispatch]);

    useEffect(() => {
        const getConfig = async () => {
            // Only load config once to prevent overwriting user changes
            if (configLoadedRef.current) return;

            setIsLoadingCustomFields(true);
            try {
                const config = await settingsAPI.findConfig()
                configLoadedRef.current = true;
                setConfigLoaded(true);
                if (config) {
                    // Load custom fields from config
                    if (config.customFields) {
                        setCustomFields(config.customFields);
                        setInitialCustomFields(config.customFields);
                    }
                    // Load visualizer filters from config
                    if (config.visualizerFilters) {
                        setVisualizerFilters(config.visualizerFilters);
                        setInitialVisualizerFilters(config.visualizerFilters); // Store initial values
                    } else {
                        // Set default filters if not configured
                        const defaultFilters = ['Color', 'Size', 'Collection'];
                        setVisualizerFilters(defaultFilters);
                        setInitialVisualizerFilters(defaultFilters); // Store initial values
                    }
                }
            } finally {
                setIsLoadingCustomFields(false);
            }
        }
        getConfig()
        // No reverting the theme on the way out: edits are saved as they are made.
    }, [])

    /*
     * Saved by itself, a moment after the last change — and only what this
     * section edits. It used to send the logo, colours and language too, from
     * copies taken when it opened; saved on their own schedule, those copies
     * could put back a colour just changed in Brand.
     */
    useAutosave({
        data: { customFields, visualizerFilters },
        ready: configLoaded,
        save: async () => {
            const currentVisualizerFilters = [...visualizerFilters];
            const currentCustomFields = [...customFields];
            await settingsAPI.updateConfig({
                customFields: currentCustomFields,
                visualizerFilters: currentVisualizerFilters,
            });

            // Products' filter values are rebuilt when the fields or filters change.
            const customFieldsChanged = JSON.stringify(initialCustomFields) !== JSON.stringify(currentCustomFields);
            const filtersChanged = JSON.stringify(initialVisualizerFilters) !== JSON.stringify(currentVisualizerFilters);
            if (customFieldsChanged || filtersChanged) {
                productsAPI.updateCustomFieldFilters().catch((error) => console.error('Error updating filters:', error));
            }
            setInitialCustomFields(currentCustomFields);
            setInitialVisualizerFilters(currentVisualizerFilters);
            dispatch(setInitialTheme());
        },
    });

    // Custom fields helper functions
    // Generate field name from field label
    const generateFieldName = (label: string): string => {
        return label
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .replace(/-+/g, '_') // Replace hyphens with underscores
            .replace(/_+/g, '_') // Replace multiple underscores with single
            .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
    };

    const handleAddCustomField = () => {
        if (newCustomField.fieldLabel) {
            const generatedFieldName = generateFieldName(newCustomField.fieldLabel);
            const fieldToAdd = {
                ...newCustomField,
                fieldName: generatedFieldName,
                // Set default value to empty array for multioption fields
                defaultValue: newCustomField.fieldType === 'multioption' ? [] : newCustomField.defaultValue
            };
            setCustomFields([...customFields, fieldToAdd]);
            setNewCustomField({
                fieldName: '',
                fieldType: 'text',
                fieldLabel: '',
                required: false,
                options: [],
                defaultValue: '',
                placeholder: ''
            });
        }
    };

    const handleRemoveCustomField = (index) => {
        setCustomFields(customFields.filter((_, i) => i !== index));
    };

    const handleUpdateCustomField = (index, field, value) => {
        const updatedFields = customFields.map((customField, i) =>
            i === index ? { ...customField, [field]: value } : customField
        );
        setCustomFields(updatedFields);
    };

    const handleAddOptionToNewField = () => {
        if (newOptionValue.trim()) {
            setNewCustomField({
                ...newCustomField,
                options: [...newCustomField.options, newOptionValue.trim()]
            });
            setNewOptionValue('');
        }
    };

    const handleRemoveOptionFromNewField = (optionIndex) => {
        setNewCustomField({
            ...newCustomField,
            options: newCustomField.options.filter((_, i) => i !== optionIndex)
        });
    };

    const handleAddOptionToExistingField = (fieldIndex, optionValue) => {
        if (optionValue.trim()) {
            const updatedFields = customFields.map((field, i) =>
                i === fieldIndex
                    ? { ...field, options: [...(field.options || []), optionValue.trim()] }
                    : field
            );
            setCustomFields(updatedFields);
        }
    };

    const handleRemoveOptionFromExistingField = (fieldIndex, optionIndex) => {
        const updatedFields = customFields.map((field, i) =>
            i === fieldIndex
                ? { ...field, options: field.options.filter((_, oi) => oi !== optionIndex) }
                : field
        );
        setCustomFields(updatedFields);
    };

    // Visualizer filters helper functions
    const handleToggleFilter = (filterName: string) => {
        if (visualizerFilters.includes(filterName)) {
            // Remove filter
            setVisualizerFilters(visualizerFilters.filter(f => f !== filterName));
        } else {
            // Add filter
            setVisualizerFilters([...visualizerFilters, filterName]);
        }
    };

    const handleFilterDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = visualizerFilters.indexOf(active.id as string);
            const newIndex = visualizerFilters.indexOf(over.id as string);
            const reordered = arrayMove(visualizerFilters, oldIndex, newIndex);
            setVisualizerFilters(reordered);
        }
    };

    const getAvailableFilters = () => {
        // Default filters
        const defaultFilters = [
            { name: 'Color', label: t('PAGES.SETTINGS.FILTER_COLOR') || 'Color' },
            { name: 'Size', label: t('PAGES.SETTINGS.FILTER_SIZE') || 'Size' },
            { name: 'Collection', label: t('PAGES.SETTINGS.FILTER_COLLECTION') || 'Collection' }
        ];

        // Add custom fields as potential filters
        const customFieldFilters = customFields.map(field => ({
            name: field.fieldName,
            label: field.fieldLabel
        }));

        return [...defaultFilters, ...customFieldFilters];
    };

    const availableFilters = getAvailableFilters();
    const unusedFilters = availableFilters.filter(f => !visualizerFilters.includes(f.name));
    const fieldTypeLabel = t('PAGES.SETTINGS.FIELD_TYPE', 'Type') as string;

    /*
     * No <form> and no page chrome: this only renders inside the Visualizer
     * studio's settings column, and it saves by itself. A form here would also
     * turn Enter in the option inputs into an implicit submit.
     */
    return (
        <div className="flex flex-col gap-8">
            <SettingsGroup
                title={t('PAGES.SETTINGS.VISUALIZER_FILTERS')}
                description={t('PAGES.SETTINGS.VISUALIZER_FILTERS_HELP')}
            >
                {visualizerFilters.length > 0 && (
                    <SettingsField label={t('PAGES.SETTINGS.SELECTED_FILTERS_ORDER') || 'Active filters (drag to reorder)'}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFilterDragEnd}>
                            <SortableContext items={visualizerFilters} strategy={verticalListSortingStrategy}>
                                <div className="flex flex-col divide-y overflow-hidden rounded-lg border">
                                    {visualizerFilters.map((filterName, index) => {
                                        const filter = availableFilters.find(f => f.name === filterName);
                                        if (!filter) return null;
                                        return (
                                            <SortableFilterRow
                                                key={filter.name}
                                                id={filter.name}
                                                filter={filter}
                                                index={index}
                                                onRemove={handleToggleFilter}
                                                removeLabel={t('PAGES.SETTINGS.REMOVE_FILTER', 'Remove filter')}
                                            />
                                        );
                                    })}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </SettingsField>
                )}

                {visualizerFilters.length === 0 && (
                    <p className="text-xs text-destructive">
                        {t('PAGES.SETTINGS.VISUALIZER_FILTERS_WARNING')}
                    </p>
                )}

                {unusedFilters.length > 0 && (
                    <SettingsField label={t('PAGES.SETTINGS.AVAILABLE_FILTERS') || 'Available filters (click to enable)'}>
                        <div className="flex flex-wrap gap-1.5">
                            {unusedFilters.map((filter) => (
                                <Button
                                    key={filter.name}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="font-normal"
                                    onClick={() => handleToggleFilter(filter.name)}
                                >
                                    <Plus className="size-3.5 text-muted-foreground" />
                                    {filter.label}
                                </Button>
                            ))}
                        </div>
                    </SettingsField>
                )}
            </SettingsGroup>

            <SettingsGroup title={t('PAGES.SETTINGS.CUSTOM_PRODUCT_FIELDS')}>
                {isLoadingCustomFields ? (
                    <div className="flex flex-col gap-2">
                        {[1, 2].map((i) => (
                            <Skeleton key={i} className="h-24 w-full rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {customFields.length > 0 ? (
                            <div className="flex flex-col divide-y rounded-lg border">
                                {customFields.map((field, index) => (
                                    <div key={index} className="flex flex-col gap-3 p-3">
                                        <div className="flex items-center gap-2">
                                            <Input
                                                value={field.fieldLabel}
                                                onChange={(e) => {
                                                    handleUpdateCustomField(index, 'fieldLabel', e.target.value);
                                                    handleUpdateCustomField(index, 'fieldName', generateFieldName(e.target.value));
                                                }}
                                                className="h-9 flex-1"
                                                placeholder={t('PAGES.SETTINGS.FIELD_LABEL_PLACEHOLDER')}
                                            />
                                            <Button
                                                type="button"
                                                size="icon-sm"
                                                variant="outline"
                                                className="shrink-0 border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                                aria-label={t('PAGES.SETTINGS.REMOVE_CUSTOM_FIELD', 'Remove custom field') as string}
                                                onClick={() => handleRemoveCustomField(index)}
                                            >
                                                <Trash2 className="size-4" />
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <ToggleGroup
                                                type="single"
                                                variant="outline"
                                                size="sm"
                                                aria-label={fieldTypeLabel}
                                                value={field.fieldType}
                                                onValueChange={(value) => value && handleUpdateCustomField(index, 'fieldType', value)}
                                            >
                                                <ToggleGroupItem value="text">{t('PAGES.SETTINGS.TEXT')}</ToggleGroupItem>
                                                <ToggleGroupItem value="select">{t('PAGES.SETTINGS.SELECT')}</ToggleGroupItem>
                                            </ToggleGroup>
                                            <Input
                                                value={field.placeholder || ''}
                                                onChange={(e) => handleUpdateCustomField(index, 'placeholder', e.target.value)}
                                                className="h-8 min-w-0 flex-1"
                                                placeholder={t('PAGES.SETTINGS.PLACEHOLDER_PLACEHOLDER')}
                                            />
                                        </div>
                                        {field.fieldType === 'select' && (
                                            <OptionsEditor
                                                label={t('PAGES.SETTINGS.OPTIONS') || 'Options'}
                                                options={field.options || []}
                                                value={editingFieldOptionValues[index] || ''}
                                                placeholder={t('PAGES.SETTINGS.ADD_OPTION') || 'Add option'}
                                                onValueChange={(v) => setEditingFieldOptionValues({ ...editingFieldOptionValues, [index]: v })}
                                                onAdd={() => {
                                                    if (editingFieldOptionValues[index]?.trim()) {
                                                        handleAddOptionToExistingField(index, editingFieldOptionValues[index]);
                                                        setEditingFieldOptionValues({ ...editingFieldOptionValues, [index]: '' });
                                                    }
                                                }}
                                                onRemove={(optionIndex) => handleRemoveOptionFromExistingField(index, optionIndex)}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <SettingsHint className="mt-0">
                                {t('PAGES.SETTINGS.NO_CUSTOM_FIELDS', 'No custom fields yet. Add one below.')}
                            </SettingsHint>
                        )}

                        {/* New field draft — same controls as a row, flat, with an add button in place of delete */}
                        <SettingsField label={t('PAGES.SETTINGS.ADD_NEW_CUSTOM_FIELD')}>
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2">
                                    <Input
                                        value={newCustomField.fieldLabel}
                                        onChange={(e) => setNewCustomField({ ...newCustomField, fieldLabel: e.target.value })}
                                        className="h-9 flex-1"
                                        placeholder={t('PAGES.SETTINGS.FIELD_LABEL_PLACEHOLDER')}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon-sm"
                                        className="shrink-0"
                                        onClick={handleAddCustomField}
                                        disabled={!newCustomField.fieldLabel}
                                        aria-label={t('PAGES.SETTINGS.ADD_CUSTOM_FIELD', 'Add field') as string}
                                    >
                                        <Plus className="size-4" />
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ToggleGroup
                                        type="single"
                                        variant="outline"
                                        size="sm"
                                        aria-label={fieldTypeLabel}
                                        value={newCustomField.fieldType}
                                        onValueChange={(value) => value && setNewCustomField({ ...newCustomField, fieldType: value, options: value === 'select' ? newCustomField.options : [] })}
                                    >
                                        <ToggleGroupItem value="text">{t('PAGES.SETTINGS.TEXT')}</ToggleGroupItem>
                                        <ToggleGroupItem value="select">{t('PAGES.SETTINGS.SELECT')}</ToggleGroupItem>
                                    </ToggleGroup>
                                    <Input
                                        value={newCustomField.placeholder}
                                        onChange={(e) => setNewCustomField({ ...newCustomField, placeholder: e.target.value })}
                                        className="h-8 min-w-0 flex-1"
                                        placeholder={t('PAGES.SETTINGS.PLACEHOLDER_PLACEHOLDER')}
                                    />
                                </div>
                                {newCustomField.fieldType === 'select' && (
                                    <OptionsEditor
                                        label={t('PAGES.SETTINGS.OPTIONS') || 'Options'}
                                        options={newCustomField.options}
                                        value={newOptionValue}
                                        placeholder={t('PAGES.SETTINGS.ADD_OPTION') || 'Add option'}
                                        onValueChange={setNewOptionValue}
                                        onAdd={handleAddOptionToNewField}
                                        onRemove={handleRemoveOptionFromNewField}
                                    />
                                )}
                            </div>
                        </SettingsField>
                    </>
                )}
            </SettingsGroup>
        </div>
    );
};
